import { sectionRenderer } from '@theme/section-renderer';

const SECTION_ATTR = 'data-featured-blog-posts-section';
const PAGINATION_SELECTOR = '.featured-blog-posts__pagination';

/** Query params used for section rendering – must not appear in address bar or full page loads break. */
const SECTION_PARAMS = ['section_id', 'sections'];

/**
 * Returns a URL with section-rendering params removed (so hard reload and sharing work).
 * @param {URL} url
 * @returns {URL}
 */
function getCleanUrl(url) {
  const clean = new URL(url.toString());
  SECTION_PARAMS.forEach((param) => clean.searchParams.delete(param));
  return clean;
}

/**
 * Handles pagination link clicks without full page reload by re-rendering
 * the section via the Section Rendering API.
 */
function init() {
  // If the page was loaded with section_id in the URL (e.g. bookmarked), clean it so hard reload works
  const currentUrl = new URL(globalThis.location.href);
  if (SECTION_PARAMS.some((param) => currentUrl.searchParams.has(param))) {
    history.replaceState(history.state, '', getCleanUrl(currentUrl).toString());
  }

  const containers = document.querySelectorAll(`[${SECTION_ATTR}]`);
  containers.forEach((container) => {
    const pagination = container.querySelector(PAGINATION_SELECTOR);
    if (!pagination) return;

    const sectionId = container.getAttribute(SECTION_ATTR);
    if (!sectionId) return;

    pagination.addEventListener('click', handlePaginationClick.bind(null, sectionId));
  });
}

/**
 * @param {string} sectionId
 * @param {MouseEvent} event
 */
async function handlePaginationClick(sectionId, event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const link = target.closest(`${PAGINATION_SELECTOR} a[href]`);
  if (!link || !(link instanceof HTMLAnchorElement)) return;
  if (link.href === '#' || link.classList.contains('pagination__link--disabled')) return;

  event.preventDefault();

  const url = new URL(link.href, globalThis.location.origin);
  const urlUnchanged =
    url.pathname === globalThis.location.pathname && url.search === globalThis.location.search;

  const container = document.querySelector(`[${SECTION_ATTR}="${sectionId}"]`);
  const wasExpanded = container
    ? (document.getElementById(`featured-blog-posts-more-${sectionId}`)?.classList.contains('featured-blog-posts__collapsible--open') ?? false)
    : false;

  if (container) {
    container.classList.add('featured-blog-posts--loading');
  }

  try {
    await sectionRenderer.renderSection(sectionId, {
      cache: true,
      url,
    });
    if (!urlUnchanged) {
      const cleanUrl = getCleanUrl(url);
      history.pushState({}, '', cleanUrl.toString());
    }

    if (wasExpanded) {
      reexpandSection(sectionId);
    }
    scrollToSectionTop(sectionId);
  } catch (err) {
    if (container) {
      container.classList.remove('featured-blog-posts--loading');
    }
    // Fallback to full page navigation if section render fails (e.g. network error)
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('Featured blog posts pagination: section render failed, falling back to full navigation.', err);
    }
    globalThis.location.href = getCleanUrl(url).toString();
  }
}

/**
 * Re-expands the View All collapsible (e.g. after pagination so it stays open).
 * @param {string} sectionId
 */
function reexpandSection(sectionId) {
  requestAnimationFrame(() => {
    const viewAllBtn = document.querySelector(
      `[${SECTION_ATTR}="${sectionId}"] .featured-blog-posts__cta--view-all [data-featured-blog-posts-toggle="${sectionId}"]`
    );
    if (viewAllBtn instanceof HTMLElement) {
      viewAllBtn.click();
    }
  });
}

/**
 * Smoothly scrolls to the top of the featured blog posts section.
 * @param {string} sectionId
 */
function scrollToSectionTop(sectionId) {
  const container = document.querySelector(`[${SECTION_ATTR}="${sectionId}"]`);
  if (container instanceof HTMLElement) {
    container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/**
 * Re-renders featured blog posts sections from current URL on popstate (back/forward).
 */
function handlePopState() {
  const containers = document.querySelectorAll(`[${SECTION_ATTR}]`);
  const currentUrl = new URL(globalThis.location.href);
  const urlForSection = getCleanUrl(currentUrl);

  containers.forEach((container) => {
    const pagination = container.querySelector(PAGINATION_SELECTOR);
    if (!pagination) return;

    const sectionId = container.getAttribute(SECTION_ATTR);
    if (!sectionId) return;

    container.classList.add('featured-blog-posts--loading');
    sectionRenderer.renderSection(sectionId, {
      cache: true,
      url: urlForSection,
    }).then(() => {
      scrollToSectionTop(sectionId);
      if (currentUrl.toString() !== urlForSection.toString()) {
        history.replaceState(history.state, '', urlForSection.toString());
      }
    }).catch(() => {
      container.classList.remove('featured-blog-posts--loading');
    });
  });
}

const POPSTATE_DATA_KEY = 'featuredBlogPostsPopState';

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    init();
    if (!document.documentElement.dataset[POPSTATE_DATA_KEY]) {
      document.documentElement.dataset[POPSTATE_DATA_KEY] = 'true';
      globalThis.addEventListener('popstate', handlePopState);
    }
  });
} else {
  init();
  if (!document.documentElement.dataset[POPSTATE_DATA_KEY]) {
    document.documentElement.dataset[POPSTATE_DATA_KEY] = 'true';
    globalThis.addEventListener('popstate', handlePopState);
  }
}
